import React, { useMemo } from "react";
import { addMinutes, format } from "date-fns";
import {
  Alert,
  AlertTitle,
  Avatar,
  Button,
  Chip,
  IconButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { orange } from "@mui/material/colors";
import { useTheme } from "@mui/styles";
import { Eye, WarningCircle, X } from "@phosphor-icons/react";

import ANMultiProgressBar from "elements/ANMultiProgressBar";
import ApprovingFlowBlock from "./ApprovingFlowBlock";
import { formatRepeatDays } from "components/EventScheduling/EventSchedulingUtils";
import ANCalendarEvent from "elements/ANCalendarEvent";
import { useMatching } from "components/Matching/MatchingProvider";
import useScheduleVideoCall from "hooks/api/appointments/useScheduleVideoCall";
import useAssignClinician from "hooks/api/customers/useAssignClinician";
import {
  datesHaveTimeOverlap,
  setScheduleDate,
} from "components/Matching/MatchingCalendar/calendarUtils";
import { createCalendarCallEvents } from "components/WeeklyCalendarSessions/CalendarUtils";
import ANTooltip from "elements/ANTooltip";
import getAvatarInitials from "utils/getAvatarInitials";

const progressBarData = {
  DIRECT_THERAPY: { color: orange[700], legend: "Direct Therapy" },
  CAREGIVER_TRAINING: { color: orange[400], legend: "Caregiver Training" },
};

const billingTypeMap = {
  CAREGIVER_TRAINING: "CT",
  DIRECT_THERAPY: "DT",
};

const DAYS = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

export default function ClientBlock({ clientSchedule, selected, onEyeButtonClick, clinicianName }) {
  const theme = useTheme();
  const { generateApproveSchedulePayload, refetchGeneratedSchedules, singleEvents } = useMatching();
  const [isSuccessful, setIsSuccessful] = React.useState(false);
  const [approvedIsTentative, setApprovedIsTentative] = React.useState(false);
  const [anchorConflicsEl, setAnchorConflicsEl] = React.useState(null);
  const [eventsConflicted, setEventsConflicted] = React.useState([]);
  const openOrdering = Boolean(anchorConflicsEl);
  const handleConflicsOpen = (event) => {
    setAnchorConflicsEl(event.currentTarget);
  };
  const handleConflicsClose = () => setAnchorConflicsEl(null);
  const { isScheduleVideoCallLoading, scheduleVideoCall } = useScheduleVideoCall();
  const { assignClinician, isAssignClinicianLoading } = useAssignClinician();
  const clientFirstName = clientSchedule.client_name.split(" ")[0];
  const ClientIconButton = selected ? X : Eye;

  const daysWithSessions = clientSchedule.eventsData
    .flatMap((event) => {
      const days = formatRepeatDays(event.repeat_days);
      return days.map((day) => {
        const conflicts = [];

        singleEvents?.forEach((singleEvent) => {
          const singleEventStartDate = new Date(singleEvent.scheduled_date);

          const scheduledDate = setScheduleDate({
            day,
            eventDate: event.scheduled_date,
          });

          const hasTimeOverlap = datesHaveTimeOverlap({
            date1: singleEventStartDate,
            date1CallDuration: singleEvent.call_duration,
            date2: scheduledDate,
            date2CallDuration: event.call_duration,
          });
          if (hasTimeOverlap) {
            conflicts.push(createCalendarCallEvents([singleEvent])[0]);
          }
        });
        return {
          day,
          duration: event.call_duration,
          billing_type: event.billing_type,
          scheduled_date: event.scheduled_date,
          call_duration: event.call_duration,
          call_timezone: event.call_timezone,
          conflicts: conflicts,
        };
      });
    })
    // Sort by day first, then by type with DIRECT_THERAPY having priority
    .sort((a, b) => {
      if (a.day !== b.day) return a.day - b.day;
      // For same day, DIRECT_THERAPY comes first
      return a.billing_type === "DIRECT_THERAPY" ? -1 : b.billing_type === "DIRECT_THERAPY" ? 1 : 0;
    });

  const closestStartDate = clientSchedule.eventsData.reduce((acc, { scheduled_date }) => {
    const date = new Date(scheduled_date);
    if (!acc) return date;
    return acc < date ? acc : date;
  }, null);

  const approveClientSchedule = async (isTentative = false) => {
    const { payload } = await generateApproveSchedulePayload({
      clientId: clientSchedule.client_id,
      setTentative: isTentative,
    });

    scheduleVideoCall(payload, {
      onSuccess: () => {
        assignClinician(
          {
            clientId: clientSchedule.client_id,
            clinicianUserId: payload?.[0].clinicianUserId,
          },
          {
            onSuccess: () => {
              setIsSuccessful(true);
              setApprovedIsTentative(isTentative);
              refetchGeneratedSchedules();
            },
          }
        );
      },
    });
  };

  const { totalHours, progressBarElements } = useMemo(() => {
    const totalMins = daysWithSessions.reduce((acc, { duration }) => acc + duration, 0);

    const typeHours = {};
    daysWithSessions.forEach(({ billing_type, duration }) => {
      typeHours[billing_type] = (typeHours[billing_type] || 0) + duration;
    });

    const elements = Object.entries(typeHours).map(([type, duration]) => ({
      color: progressBarData[type].color,
      legend: progressBarData[type].legend,
      percentage: (duration / totalMins) * 100,
      value: `${duration / 60} hours`,
    }));
    return { totalHours: totalMins / 60, progressBarElements: elements };
  }, [clientSchedule.eventsData]);

  if (isScheduleVideoCallLoading || isAssignClinicianLoading || isSuccessful) {
    return (
      <ApprovingFlowBlock
        isLoading={isScheduleVideoCallLoading || isAssignClinicianLoading}
        isSuccessful={isSuccessful}
        isTentative={approvedIsTentative}
      />
    );
  }

  return (
    <Stack
      p={5}
      gap={5}
      bgcolor="default.hover"
      borderRadius={5}
      sx={{ "&:hover": { bgcolor: "action.selected" } }}
    >
      <Stack direction="row" justifyContent="space-between" gap={3} alignItems="center">
        <Stack direction="row" gap={3} alignItems="center" maxWidth="50%">
          <Avatar size="24px">{getAvatarInitials(clientSchedule?.client_name)}</Avatar>
          <ANTooltip component={Typography} variant="body2">
            {clientSchedule.client_name}
          </ANTooltip>
        </Stack>
        <Stack direction="row" gap={3} alignItems="center" maxWidth="50%">
          <ANTooltip component={Typography} variant="body2" color="text.secondary">
            Starts {format(closestStartDate, "MMM do")}
          </ANTooltip>
          <Tooltip title={`Only show ${clientFirstName}’s schedule`}>
            <IconButton fontSize="small" size="small" onClick={onEyeButtonClick}>
              <ClientIconButton color={theme.palette.text.secondary} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack direction={"row"} gap={3} alignItems="center">
        <Stack flexGrow={1}>
          <ANMultiProgressBar
            roundElement
            gap={4}
            backgroundColor="unset"
            elements={progressBarElements}
          />
        </Stack>
        <Typography variant="subtitle2" color="primary.main">
          {totalHours} Hours
        </Typography>
      </Stack>

      <Stack direction={"row"} justifyContent="space-between">
        {["M", "T", "W", "T", "F", "S", "S"].map((day, index) => (
          <Chip
            key={index}
            label={day}
            color={
              daysWithSessions.some((session) => session.day === index + 1) ? "primary" : "default"
            }
            sx={{ width: 32 }}
          />
        ))}
      </Stack>

      <Stack gap={1}>
        {daysWithSessions.map(
          ({ billing_type, scheduled_date, call_duration, day, conflicts }, index) => {
            const hasConflict = conflicts && conflicts.length > 0;
            const scheduledDate = new Date(scheduled_date);

            return (
              <Stack
                key={index}
                direction={"row"}
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color={hasConflict ? "error.main" : "text.primary"}>
                  {DAYS[day]} ({billingTypeMap[billing_type]})
                </Typography>

                <Stack direction="row" gap={1} alignItems="center">
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={hasConflict ? "error.main" : "primary.main"}
                  >
                    {format(scheduledDate, "h:mm")} -{" "}
                    {format(addMinutes(scheduledDate, call_duration), "p")}
                  </Typography>

                  {hasConflict && (
                    <IconButton
                      fontSize="small"
                      size="small"
                      color="error"
                      onClick={(e) => {
                        setEventsConflicted(conflicts);
                        handleConflicsOpen(e);
                      }}
                    >
                      <WarningCircle weight="duotone" color={theme.palette.error.main} />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            );
          }
        )}
      </Stack>
      <Stack direction={"row"} gap={3}>
        <Button sx={{ flex: "auto" }} size="small" onClick={() => approveClientSchedule()}>
          Approve Schedule
        </Button>
        <Button
          sx={{ flex: "auto" }}
          color="secondary"
          size="small"
          onClick={() => approveClientSchedule(true)}
        >
          Set as Tentative
        </Button>
      </Stack>
      <Menu
        id="conflicts-menu"
        anchorEl={anchorConflicsEl}
        open={openOrdering}
        onClose={handleConflicsClose}
        MenuListProps={{ "aria-labelledby": "open-ordering" }}
        sx={{ "& ul": { padding: 0 } }}
      >
        <Stack m={7} gap={5} width={396}>
          <Alert severity="error">
            <AlertTitle>Appointment Conflict</AlertTitle>
            This appointment conflicts with other nonrecurring events on{" "}
            {clinicianName?.split(" ")[0]}’s calendar. Approving the schedule will ignore the
            conflicts.
          </Alert>
          <Stack gap={3} sx={{ ...theme.bigCalendarCustomStyle }}>
            {eventsConflicted?.map((event, index) => (
              <Stack
                key={index}
                className="overlay-rbc-event rbc-event event-color-0"
                style={{ padding: 0 }}
                onClick={() => {
                  window.open(
                    `/clinician-schedule/create-event?id=${
                      event.id
                    }&isNonVideoEvent=${!event.is_video_call}`,
                    "_blank"
                  );
                }}
              >
                <section className="rbc-event-content h100">
                  <ANCalendarEvent event={event} view="DAY" fixedSize="large" dateFormat="date" />
                </section>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Menu>
    </Stack>
  );
}
